import React, { useEffect, useRef, useState, useCallback } from 'react';
import Hls from 'hls.js';

export const VideoPlayer = ({
  url,
  autoPlay = false,
  muted = false,
  controls = true,
  poster,
  loop = false,
  maxBufferLength = 30,
  maxMaxBufferLength = 60,
  backBufferLength = 10,
  onReady,
  onError,
  className,
  style,
  ...rest
}) => {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);

  const [ready, setReady] = useState(false); // metadata loaded
  const [err, setErr] = useState(null);
  const [started, setStarted] = useState(autoPlay); // user initiated or autoPlay
  const [initializing, setInitializing] = useState(false);

  const isHlsStream = typeof url === 'string' && url.endsWith('.m3u8');

  // Detect native HLS support (Safari / iOS)
  const canNativeHls = () => {
    if (typeof document === 'undefined') return false;
    const v = document.createElement('video');
    return (
      v.canPlayType('application/vnd.apple.mpegurl') ||
      v.canPlayType('application/x-mpegURL')
    );
  };

  const cleanupHls = () => {
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
  };

  const handleLoaded = () => {
    setReady(true);
    onReady && onReady();
    if (started) {
      videoRef.current?.play().catch(() => {
        /* autoplay blocked */
      });
    }
  };

  const handleVideoError = (e) => {
    const msg =
      e?.message ||
      (e?.target && e.target.error && e.target.error.message) ||
      'Playback error';
    setErr(msg);
    onError && onError(e);
  };

  // Initialize only after user starts (or autoPlay)
  useEffect(() => {
    if (!started) return;
    if (!url) return;

    const video = videoRef.current;
    if (!video) return;

    setErr(null);
    setReady(false);
    setInitializing(true);
    cleanupHls();

    if (isHlsStream && !canNativeHls() && Hls.isSupported()) {
      // Use Hls.js (load only now)
      const hls = new Hls({
        autoStartLoad: true, // we start playback immediately after manifest parse
        maxBufferLength,
        maxMaxBufferLength,
        backBufferLength,
      });
      hlsRef.current = hls;

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        setInitializing(false);
        handleLoaded();
      });

      hls.on(Hls.Events.ERROR, (_, data) => {
        if (data?.fatal) {
          const msg =
            (data.error && data.error.message) ||
            data?.details ||
            'HLS fatal error';
          setErr(msg);
          if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
            hls.startLoad();
          } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
            hls.recoverMediaError();
          } else {
            hls.destroy();
          }
          onError && onError(data);
        }
      });

      hls.attachMedia(video);
      hls.loadSource(url);
    } else {
      // Native (Safari) or non-HLS
      video.preload = 'metadata';
      video.src = url;
      video.addEventListener('loadedmetadata', handleLoaded);
      video.addEventListener('error', handleVideoError);
    }

    return () => {
      const v = videoRef.current;
      cleanupHls();
      if (v) {
        v.removeEventListener('loadedmetadata', handleLoaded);
        v.removeEventListener('error', handleVideoError);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    started,
    url,
    isHlsStream,
    maxBufferLength,
    maxMaxBufferLength,
    backBufferLength,
  ]);

  const onUserStart = useCallback(() => {
    if (started) return;
    setStarted(true);
  }, [started]);

  if (!url) {
    return <div style={{ color: 'red' }}>Video not supported</div>;
  }

  const showOverlayPlay = !started && !err;

  return (
    <div
      className={className}
      style={{
        position: 'relative',
        background: '#000',
        maxWidth: '100%',
        ...style,
      }}
      {...rest}
    >
      {showOverlayPlay && (
        <button
          type="button"
          onClick={onUserStart}
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,0,0,0.55)',
            color: '#fff',
            fontSize: 16,
            border: 'none',
            cursor: 'pointer',
            zIndex: 2,
          }}
          aria-label="Play video"
        >
          ▶ Play
        </button>
      )}

      {!ready && started && !err && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: 14,
            zIndex: 1,
          }}
        >
          {initializing ? 'Loading…' : 'Preparing…'}
        </div>
      )}

      {err && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#ffdddd',
            background: '#330000',
            padding: 12,
            textAlign: 'center',
            fontSize: 13,
            zIndex: 2,
          }}
        >
          {String(err)}
        </div>
      )}

      <video
        ref={videoRef}
        // Do NOT set src until started for native HLS / MP4 case (handled in effect)
        poster={poster}
        muted={muted}
        loop={loop}
        controls={controls && started} // hide native controls until play
        playsInline
        preload="none" // prevent any preload before start
        style={{
          width: '640px',
          height: '360px',
          display: 'block',
          opacity: started ? 1 : 0.85,
        }}
      />
    </div>
  );
};
